/**
 * Drone registry for the site. Reads droneList()/droneConfig() from the diamond (silently empty if the
 * DroneFacet is not cut in yet) and names from the keeper's GET /drones when VITE_KEEPER_URL is set.
 * formatAddress() consults this cache synchronously, so every shortened wallet gets the 🤖 tag.
 */
import { useEffect, useState } from 'react';
import { ethers } from 'ethers';
import { getReadProvider } from './contract';
import { CHAIN } from '../config/chain';
import { DRONE_PERSONAS, type DronePersona } from '../config/drones';

export const DRONE_VIEW_ABI = [
  'function droneList() view returns (address[])',
  'function droneConfig() view returns (bool enabled, uint256 count)',
  'function roundHumans(uint256 roundId) view returns (uint256 humans, uint256 drones)',
  'function bossDrone() view returns (address)',
  'function roundMode(uint256 roundId) view returns (uint8)',
];

/** Round mode bits (contracts/phase2/LibDrones.sol). VEX may only buy in a FINALE round. */
export const MODE_FINALE = 1, MODE_BLACKOUT = 2, MODE_OVERCLOCK = 4;
export function modeLabels(mode: number): string[] {
  const out: string[] = [];
  if (mode & MODE_FINALE) out.push('Finale');
  if (mode & MODE_BLACKOUT) out.push('Blackout');
  if (mode & MODE_OVERCLOCK) out.push('Overclock');
  return out;
}

const map = new Map<string, DronePersona>();
let enabled = false;
let loaded: Promise<void> | null = null;
const subs = new Set<() => void>();
const emit = () => subs.forEach((f) => f());

export function droneOf(addr?: string | null): DronePersona | null {
  return addr ? map.get(addr.toLowerCase()) ?? null : null;
}
export const dronesEnabled = () => enabled;
export const droneCount = () => map.size;

export function droneContract(): ethers.Contract {
  return new ethers.Contract(CHAIN.gameAddress, DRONE_VIEW_ABI, getReadProvider());
}

export function loadDrones(force = false): Promise<void> {
  if (loaded && !force) return loaded;
  loaded = (async () => {
    if (!CHAIN.gameAddress) return;
    const c = droneContract();
    let list: string[] = [];
    let boss = '';
    try {
      const [cfg, l] = await Promise.all([c.droneConfig(), c.droneList()]);
      enabled = Boolean(cfg.enabled ?? cfg[0]);
      list = (l as string[]).map((a) => a.toLowerCase());
    } catch { return; /* DroneFacet not cut in yet */ }
    try { const b = String(await c.bossDrone()).toLowerCase(); if (!/^0x0{40}$/.test(b)) boss = b; } catch { /* Phase 2.8 not cut in yet */ }
    const named = new Map<string, string>();
    const keeper = import.meta.env.VITE_KEEPER_URL as string | undefined;
    if (keeper) {
      try {
        const r = await fetch(`${keeper.replace(/\/$/, '')}/drones`, { signal: AbortSignal.timeout(6000) });
        const d = await r.json() as { address: string; name: string }[];
        if (Array.isArray(d)) d.forEach((x) => named.set(x.address.toLowerCase(), x.name));
      } catch { /* fall back to registry order */ }
    }
    map.clear();
    list.forEach((a, i) => {
      const p = DRONE_PERSONAS.find((x) => x.name === named.get(a)) ?? DRONE_PERSONAS[i % DRONE_PERSONAS.length];
      map.set(a, p);
    });
    // VEX sits outside the 16-slot list; it is still a drone and can never win
    const vex = DRONE_PERSONAS.find((x) => x.boss);
    if (boss && vex && !map.has(boss)) map.set(boss, vex);
    emit();
  })();
  return loaded;
}

/** Re-render when the registry loads. */
export function useDrones(): { droneOf: typeof droneOf; enabled: boolean; count: number } {
  const [, set] = useState(0);
  useEffect(() => {
    const f = () => set((n) => n + 1);
    subs.add(f);
    void loadDrones();
    return () => { subs.delete(f); };
  }, []);
  return { droneOf, enabled, count: map.size };
}

/** Short wallet label: "🤖 NAME" for a registered drone, else 0x1234...abcd. */
export function shortWallet(addr?: string | null): string {
  if (!addr) return '';
  const d = droneOf(addr);
  if (d) return `🤖 ${d.name}`;
  return addr.length > 12 ? `${addr.slice(0, 6)}...${addr.slice(-4)}` : addr;
}
